import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  useAccountStore,
  useTikTokAccounts,
  isTikTokAccountId,
} from '../../entities/account';
import { TopVideosReportCard } from '../../features/statistics/components/TopVideosReportCard';
import { ActReportCard, PresentationReportCard, SeedingSheetReportCard } from '../../features/reports';
import { SeedingReportCard } from '../../features/seeding-report';
import {
  UnifiedAccountSelect,
  buildAccountOptions,
  type AccountOption,
} from '../../features/statistics/components/UnifiedAccountSelect';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner@2.0.3';
import { format } from 'date-fns';
import type { AccountStatRow } from '../../features/statistics/lib/stat-row';
import { reportRowFromStat } from '../../features/statistics/lib/report-row';
import { PermalinkCell } from '../../shared/ui/PermalinkCell';
import { useRemembered } from '../../shared/lib/remembered';
import { enrichReportRows, usesCaptionHeadline } from '../../features/statistics/lib/enrich-report-rows';
import { collectInstagramStats, collectTikTokStats } from '../../features/statistics/lib/collect-account-stats';
import { fetchPaidStats, paidForRow } from '../../features/statistics/api/ads-paid-stats';
import { ORGANIC_LABEL, PAID_LABEL, PaidSplitCell } from '../../features/statistics/components/PaidSplitCell';
import { generateAccountReportXlsx } from '../../features/statistics/lib/export-account-report-xlsx';
import { buildAccountReportDocx, generateAccountReportDocx } from '../../features/statistics/lib/export-account-report-docx';
import { buildLinksDocxFiles, generateLinksDocx } from '../../features/statistics/lib/export-links-docx';
import { openDocxInGoogleDocs, requestGoogleDocsToken } from '../../features/statistics/lib/open-docx-in-gdocs';
import { GoogleDocsLinks } from '../../features/statistics/components/GoogleDocsLinks';
import { MinisterLinksReport } from '../../features/statistics/components/MinisterLinksReport';
import { DateRangeSelector } from '../../features/statistics/components/DateRangeSelector';
import {
  ExportMenu,
  FORMAT_DOCX,
  FORMAT_GDOC,
  FORMAT_XLSX,
  type ExportFormat,
} from '../../features/statistics/components/ExportMenu';
import { useDateRangeParams } from '../../features/statistics/hooks/useDateRangeParams';
import { useStatsAccount } from '../../features/statistics/hooks/useStatsAccount';
import { collectionKey } from '../../features/statistics/lib/collection-key';
import { periodDays } from '../../features/statistics/lib/date-range';

/** Что именно выгружаем. Первые три берут одни и те же собранные данные. */
export type ReportKind =
  | 'xlsx'
  | 'docx'
  | 'links'
  | 'top'
  | 'seeding'
  | 'presentation'
  | 'seeding-sheet'
  | 'act';

type AccountReportKind = 'xlsx' | 'docx' | 'links';

/** Форматы трёх отчётов по аккаунту: у каждого — свои, подписи общие. */
const ACCOUNT_REPORT_FORMATS: Record<AccountReportKind, ExportFormat[]> = {
  xlsx: [FORMAT_XLSX],
  docx: [FORMAT_GDOC, FORMAT_DOCX],
  links: [
    { ...FORMAT_GDOC, description: 'Два документа в Google Диске: со скринами и без' },
    { ...FORMAT_DOCX, description: 'Два файла .docx: со скринами и без' },
  ],
};

/** Карточки, которые держат введённое у себя: при переключении их не размонтируем. */
const STANDALONE: ReportKind[] = ['top', 'seeding', 'presentation', 'seeding-sheet', 'act'];

export default function AccountsStatsPage({ report = 'xlsx' }: { report?: ReportKind }) {
  const { accounts } = useAccountStore();

  // Подключённые TikTok аккаунты (для единого списка вместе с Instagram).
  const { accounts: tiktokAccounts } = useTikTokAccounts();

  // Единый список аккаунтов (Instagram + TikTok) для селектора отчёта.
  const accountOptions: AccountOption[] = buildAccountOptions([...accounts, ...tiktokAccounts]);

  // Аккаунт и период помнятся и приходят ссылкой (?account=, период раздела):
  // раньше их выбирали заново на каждом заходе.
  const [selectedAccountId, setSelectedAccountId] = useStatsAccount();
  const [periodOption, setPeriodOption] = useDateRangeParams();
  const period = periodDays(periodOption);
  const currentKey = selectedAccountId ? collectionKey(selectedAccountId, period) : '';

  const [isLoadingAccountStat, setIsLoadingAccountStat] = useState(false);
  // Собранное помнит, для какого аккаунта и периода оно собрано. Смена
  // аккаунта или периода прячет чужие цифры: иначе выгрузка уходила под
  // новым именем со старыми данными.
  const [collected, setCollected] = useState<{ key: string; rows: any[] }>({ key: '', rows: [] });
  const accountStatResults = collected.key === currentKey ? collected.rows : [];
  // Разбивка показывается, только если реклама реально пришла хотя бы по одному
  // посту: у аккаунтов без подключённого кабинета таблица остаётся прежней.
  const showPaidSplit = accountStatResults.some((r: AccountStatRow) => r.paid != null);
  const totals = sumStats(accountStatResults);
  // TikTok сохранений не отдаёт — плитку с нулём не показываем.
  const isTikTok = isTikTokAccountId(selectedAccountId);

  const [accountStatProgress, setAccountStatProgress] = useState('');
  // Excel, Word-сводка и «ссылки и темы» собираются из одного набора данных,
  // поэтому сам сборщик у них общий — различается только кнопка выгрузки.
  // Сводка собирается двумя путями: по аккаунту за период или по
  // списку ссылок (свои паблики, партнёры, YouTube) — переключатель над формой.
  const [ministerMode, setMinisterMode] = useRemembered<'account' | 'links'>('report:docx-mode', 'account');
  const isMinisterLinks = report === 'docx' && ministerMode === 'links';
  const isAccountReport = (report === 'xlsx' || report === 'docx' || report === 'links') && !isMinisterLinks;
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportProgress, setReportProgress] = useState<{ done: number; total: number } | null>(null);
  const [docUrls, setDocUrls] = useState<string[]>([]);
  // Ссылки «Сводки» не должны всплыть под «Ссылками и темами» и наоборот.
  useEffect(() => setDocUrls([]), [report]);

  // Карточки со своим вводом (ссылки, скрины, собранные посевы) монтируются
  // при первом открытии и дальше только прячутся: переключение на соседний
  // отчёт не должно стирать то, что в них вставили.
  const [visited, setVisited] = useState<Set<ReportKind>>(() => new Set([report]));
  useEffect(() => {
    setVisited((prev) => (prev.has(report) ? prev : new Set(prev).add(report)));
  }, [report]);
  const [ministerLinksVisited, setMinisterLinksVisited] = useState(isMinisterLinks);
  useEffect(() => {
    if (isMinisterLinks) setMinisterLinksVisited(true);
  }, [isMinisterLinks]);

  const busy = isLoadingAccountStat || isGenerating;
  const canCollect = Boolean(selectedAccountId) && !busy;

  // Сбор статистики выбранного аккаунта за период. Возвращает строки или
  // null, если собирать нечего; результат запоминается вместе с ключом.
  const collectAccountStatistics = async (): Promise<any[] | null> => {
    if (!selectedAccountId) {
      toast.error('Выберите аккаунт');
      return null;
    }
    const accountId = selectedAccountId;
    const range = { from: period.from, to: period.to };
    const key = collectionKey(accountId, range);

    setIsLoadingAccountStat(true);
    setAccountStatProgress('Инициализация...');

    // TikTok аккаунт (id вида "tt:<openId>") — читаем видео за период и мапим в
    // ту же форму результатов, что Instagram (downstream таблица + отчёты общие).
    if (isTikTokAccountId(accountId)) {
      try {
        setAccountStatProgress('Загрузка видео TikTok...');
        const { rows: results } = await collectTikTokStats(accountId, range);
        setCollected({ key, rows: results });
        if (results.length === 0) toast.info('За выбранный период видео не найдено');
        else toast.success(`✅ Собрана статистика для ${results.length} видео`);
        return results;
      } catch (error) {
        toast.error(`Ошибка: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return null;
      } finally {
        setIsLoadingAccountStat(false);
        setAccountStatProgress('');
      }
    }

    try {
      const account = accounts.find(acc => acc.id === accountId);

      if (!account) {
        toast.error('Аккаунт не найден');
        return null;
      }

      console.log(`[Account Stats] Starting collection for @${account.username || account.name}`);
      setAccountStatProgress('Загрузка публикаций...');

      // Сервер, при сбое — браузер: подробности в collect-account-stats.
      const outcome = await collectInstagramStats(account, range, setAccountStatProgress);

      if (outcome.total === 0) {
        toast.error('Публикации не найдены за указанный период');
        setCollected({ key, rows: [] });
        return [];
      }

      const { rows: results, noInsights, threw } = outcome;

      // Платная часть приходит отдельным запросом и ложится поверх готовых
      // строк: органику она не трогает, а без неё таблица выглядит как раньше.
      setAccountStatProgress('Загрузка данных рекламы...');
      const paidResponse = await fetchPaidStats({ accountId: account.id, from: range.from, to: range.to });
      if (paidResponse.enabled) {
        for (const row of results) {
          row.paid = paidForRow(paidResponse, row);
        }
      }

      setCollected({ key, rows: results });
      const lost = noInsights + threw;
      if (lost > 0) {
        toast.warning(
          `Метрики получены по ${outcome.total - lost} из ${outcome.total}. ` +
          `Остальные учтены нулями — суммы будут занижены. Подробности в консоли.`,
        );
      } else {
        toast.success(`✅ Собрана статистика для ${results.length} публикаций`);
      }
      console.log(`[Account Stats] ✅ COMPLETED: ${results.length} publications processed`);
      return results;
    } catch (error) {
      console.error('[Account Stats] ❌ Fatal error:', error);
      toast.error(`Ошибка: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    } finally {
      setIsLoadingAccountStat(false);
      setAccountStatProgress('');
    }
  };

  // Формирует данные отчёта: скрин (хостинг на бэке) + тема (OCR vision через edge-функцию) + язык.
  // Возвращает accountTitle/period/rows для генератора (xlsx или docx).
  const collectReportData = async (stats: any[]): Promise<{
    accountTitle: string;
    period: string;
    rows: {
      permalink: string;
      publishedAt: string;
      views: number;
      theme: string;
      language: string;
      imageUrl: string;
    }[];
  }> => {
    const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);
    const useCaptionHeadline = usesCaptionHeadline(selectedAccount?.username);

    const enriched = await enrichReportRows(
      stats.map((r) => ({
        mediaId: String(r.mediaId),
        thumbnailUrl: r.thumbnailUrl || undefined,
        caption: r.caption,
      })),
      { useCaptionHeadline, onProgress: (done, total) => setReportProgress({ done, total }) },
    );

    const rows = stats.map((r) =>
      reportRowFromStat(
        r,
        enriched.get(String(r.mediaId))?.theme ?? '',
        enriched.get(String(r.mediaId))?.imageUrl || r.thumbnailUrl || '',
      ),
    );

    const username = isTikTokAccountId(selectedAccountId)
      ? (() => {
          const t = tiktokAccounts.find((t) => t.id === selectedAccountId);
          return t?.username ? `@${t.username}` : t?.name || 'tiktok';
        })()
      : (() => {
          const account = accounts.find((acc) => acc.id === selectedAccountId);
          return account?.username || account?.name || 'account';
        })();
    const periodLabel = `${format(period.from, 'dd.MM.yyyy')}-${format(period.to, 'dd.MM.yyyy')}`;

    return { accountTitle: username, period: periodLabel, rows };
  };

  /**
   * «Сформировать»: одна кнопка вместо двух шагов. Если статистика за этот
   * аккаунт и период уже собрана — берём её, иначе собираем, и сразу файл.
   */
  const handleGenerate = async (kind: AccountReportKind, target: string) => {
    if (!selectedAccountId || busy) return;
    setIsGenerating(true);
    setDocUrls([]);
    try {
      // Вход в Google — до сбора: после десятков секунд сбора и OCR окно входа заблокируют.
      const token = target === 'gdoc' ? await requestGoogleDocsToken() : null;
      const stats = accountStatResults.length > 0 ? accountStatResults : await collectAccountStatistics();
      if (!stats || stats.length === 0) return;
      const data = await collectReportData(stats);

      if (kind === 'xlsx') {
        await generateAccountReportXlsx(data);
        toast.success('Отчёт сформирован');
      } else if (kind === 'docx') {
        if (token) setDocUrls(await openDocxInGoogleDocs(token, [await buildAccountReportDocx(data)]));
        else await generateAccountReportDocx(data);
        toast.success('Отчёт сформирован');
      } else {
        // «Ссылки и темы» — два файла Word, которые в медиа собирали вручную скриптом
        // xlsx_to_word/gen.py: со скринами и без.
        if (token) setDocUrls(await openDocxInGoogleDocs(token, await buildLinksDocxFiles(data)));
        else await generateLinksDocx(data);
        toast.success('Готово: два документа — со скринами и без');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Не удалось сформировать отчёт');
    } finally {
      setIsGenerating(false);
      setReportProgress(null);
    }
  };

  const accountKind: AccountReportKind = report === 'docx' || report === 'links' ? report : 'xlsx';

  return (
    <div className="flex flex-col gap-4">
      {STANDALONE.map((kind) =>
        visited.has(kind) ? (
          <div key={kind} className={report === kind ? undefined : 'hidden'}>
            {kind === 'top' && <TopVideosReportCard accounts={[...accounts, ...tiktokAccounts]} />}
            {kind === 'seeding' && <SeedingReportCard />}
            {kind === 'presentation' && <PresentationReportCard />}
            {kind === 'seeding-sheet' && <SeedingSheetReportCard />}
            {kind === 'act' && <ActReportCard />}
          </div>
        ) : null,
      )}

      {report === 'docx' && (
        <div className="inline-flex w-fit rounded-lg border p-1 gap-1" role="tablist">
          {(
            [
              ['account', 'Аккаунт за период'],
              ['links', 'Список ссылок'],
            ] as const
          ).map(([mode, label]) => (
            <Button
              key={mode}
              role="tab"
              aria-selected={ministerMode === mode}
              size="sm"
              variant={ministerMode === mode ? 'default' : 'ghost'}
              onClick={() => setMinisterMode(mode)}
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      {ministerLinksVisited && (
        <div className={isMinisterLinks ? undefined : 'hidden'}>
          <MinisterLinksReport />
        </div>
      )}

      <div className={isAccountReport ? 'space-y-6' : 'hidden'}>
          <Card>
            <CardHeader>
              <CardTitle>Аккаунт и период</CardTitle>
              <CardDescription>
                «Показать статистику» — цифры по всем публикациям периода прямо здесь.
                «Сформировать» выгрузит их файлом (соберёт сам, если ещё не собрано).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
                <div className="space-y-2 min-w-0">
                  <Label>Аккаунт</Label>
                  <UnifiedAccountSelect
                    options={accountOptions}
                    value={selectedAccountId}
                    onChange={setSelectedAccountId}
                    disabled={busy}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Период</Label>
                  <DateRangeSelector
                    value={periodOption}
                    onChange={setPeriodOption}
                    disabled={busy}
                    customFirst
                    className="w-full"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row">
                {/* Чаще заходят посмотреть цифры, чем за файлом, — эта кнопка главная. */}
                <Button
                  onClick={() => void collectAccountStatistics()}
                  disabled={!canCollect}
                  className="gap-2 sm:flex-1"
                >
                  {isLoadingAccountStat ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {accountStatResults.length > 0 ? 'Обновить статистику' : 'Показать статистику'}
                </Button>
                <ExportMenu
                  key={accountKind}
                  rememberKey={`report:${accountKind}`}
                  label="Сформировать"
                  formats={ACCOUNT_REPORT_FORMATS[accountKind]}
                  onSelect={(target) => handleGenerate(accountKind, target)}
                  disabled={!canCollect}
                  busy={busy}
                  busyLabel={isLoadingAccountStat ? 'Собираем статистику…' : 'Формируем отчёт…'}
                  variant="outline"
                  className="sm:flex-1"
                />
              </div>

              {accountStatProgress && (
                <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">
                  {accountStatProgress}
                </div>
              )}
              {isGenerating && reportProgress && (
                <div className="text-sm text-muted-foreground">
                  Формирование отчёта: {reportProgress.done} / {reportProgress.total}
                </div>
              )}
              {!isGenerating && report !== 'xlsx' && (
                <GoogleDocsLinks
                  urls={docUrls}
                  labels={docUrls.length === 2 ? ['без скринов', 'со скринами'] : undefined}
                />
              )}
            </CardContent>
          </Card>

          {accountStatResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Что попадёт в отчёт</CardTitle>
                <CardDescription>
                  Статистика по {accountStatResults.length} публикациям за период {format(period.from, 'dd.MM.yyyy')} — {format(period.to, 'dd.MM.yyyy')}
                  {showPaidSplit && (
                    <span className="block mt-1">
                      <span className="font-medium">{ORGANIC_LABEL}</span> — органика ·{' '}
                      <span className="font-medium">{PAID_LABEL}</span> — продвижение, по данным
                      рекламного кабинета
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Итоги — сверху: за ними и заходят, а строка «Итого» была под всей таблицей. */}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
                  <TotalTile label="Публикаций" value={accountStatResults.length} />
                  <TotalTile label="Просмотры" value={totals.views} paid={showPaidSplit ? totals.paid.views : null} />
                  <TotalTile label="Лайки" value={totals.likes} paid={showPaidSplit ? totals.paid.likes : null} />
                  <TotalTile label="Комментарии" value={totals.comments} paid={showPaidSplit ? totals.paid.comments : null} />
                  <TotalTile label="Репосты" value={totals.shares} paid={showPaidSplit ? totals.paid.shares : null} />
                  {!isTikTok && (
                    <TotalTile label="Сохранения" value={totals.saved} paid={showPaidSplit ? totals.paid.saved : null} />
                  )}
                  <TotalTile label="Охват" value={totals.reach} paid={showPaidSplit ? totals.paid.reach : null} />
                  <TotalTile label="ER" text={totals.er === null ? '—' : `${totals.er.toFixed(2)}%`} />
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ссылка</TableHead>
                        <TableHead>Тип</TableHead>
                        <TableHead>Дата</TableHead>
                        <TableHead className="text-right">Просмотры</TableHead>
                        <TableHead className="text-right">Лайки</TableHead>
                        <TableHead className="text-right">Комментарии</TableHead>
                        <TableHead className="text-right">Репосты</TableHead>
                        <TableHead className="text-right">Сохранения</TableHead>
                        <TableHead className="text-right">Охват</TableHead>
                        <TableHead className="text-right">ER</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accountStatResults.map((result, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <PermalinkCell url={result.permalink} />
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{result.mediaType}</Badge>
                          </TableCell>
                          <TableCell>
                            {new Date(result.timestamp).toLocaleString('ru-RU')}
                          </TableCell>
                          <TableCell className="text-right align-top">
                            <PaidSplitCell
                              organic={result.views}
                              paid={result.paid?.views}
                              showPaid={showPaidSplit}
                            />
                          </TableCell>
                          <TableCell className="text-right align-top">
                            <PaidSplitCell
                              organic={result.likes}
                              paid={result.paid?.likes}
                              showPaid={showPaidSplit}
                            />
                          </TableCell>
                          <TableCell className="text-right align-top">
                            <PaidSplitCell
                              organic={result.comments}
                              paid={result.paid?.comments}
                              showPaid={showPaidSplit}
                            />
                          </TableCell>
                          <TableCell className="text-right align-top">
                            <PaidSplitCell
                              organic={result.shares}
                              paid={result.paid?.shares}
                              showPaid={showPaidSplit}
                            />
                          </TableCell>
                          <TableCell className="text-right align-top">
                            <PaidSplitCell
                              organic={result.saved}
                              paid={result.paid?.saved}
                              showPaid={showPaidSplit}
                            />
                          </TableCell>
                          <TableCell className="text-right align-top">
                            <PaidSplitCell
                              organic={result.reach}
                              paid={result.paid?.reach}
                              showPaid={showPaidSplit}
                            />
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {result.er.toFixed(2) + '%'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow className="bg-muted/50">
                        <TableCell colSpan={3}>
                          <span className="font-semibold">Итого ({accountStatResults.length} публикаций)</span>
                        </TableCell>
                        <TableCell className="text-right font-semibold align-top">
                          <PaidSplitCell
                            organic={totals.views}
                            paid={showPaidSplit ? totals.paid.views : null}
                            showPaid={showPaidSplit}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold align-top">
                          <PaidSplitCell
                            organic={totals.likes}
                            paid={showPaidSplit ? totals.paid.likes : null}
                            showPaid={showPaidSplit}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold align-top">
                          <PaidSplitCell
                            organic={totals.comments}
                            paid={showPaidSplit ? totals.paid.comments : null}
                            showPaid={showPaidSplit}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold align-top">
                          <PaidSplitCell
                            organic={totals.shares}
                            paid={showPaidSplit ? totals.paid.shares : null}
                            showPaid={showPaidSplit}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold align-top">
                          <PaidSplitCell
                            organic={totals.saved}
                            paid={showPaidSplit ? totals.paid.saved : null}
                            showPaid={showPaidSplit}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold align-top">
                          <PaidSplitCell
                            organic={totals.reach}
                            paid={showPaidSplit ? totals.paid.reach : null}
                            showPaid={showPaidSplit}
                          />
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {totals.er === null ? '—' : `${totals.er.toFixed(2)}%`}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
      </div>
    </div>
  );
}

type Metric = 'views' | 'likes' | 'comments' | 'shares' | 'saved' | 'reach';
const METRICS: Metric[] = ['views', 'likes', 'comments', 'shares', 'saved', 'reach'];

/** Суммы по собранным публикациям: органика, продвижение отдельно и средний ER. */
export function sumStats(rows: AccountStatRow[]): Record<Metric, number> & {
  paid: Record<Metric, number>;
  er: number | null;
} {
  const organic = Object.fromEntries(METRICS.map((m) => [m, 0])) as Record<Metric, number>;
  const paid = { ...organic };
  let engagement = 0;
  let denominator = 0;
  for (const r of rows) {
    for (const m of METRICS) {
      organic[m] += r[m] ?? 0;
      paid[m] += r.paid?.[m] ?? 0;
    }
    engagement += r.likes + r.comments + r.shares + r.saved;
    denominator += r.views || r.reach;
  }
  return { ...organic, paid, er: denominator === 0 ? null : (engagement / denominator) * 100 };
}

function TotalTile({
  label,
  value,
  paid,
  text,
}: {
  label: string;
  value?: number;
  /** Продвижение — строкой ниже, только когда реклама пришла. */
  paid?: number | null;
  text?: string;
}) {
  return (
    <div className="rounded-lg border bg-muted/30 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{text ?? (value ?? 0).toLocaleString('ru-RU')}</div>
      {paid != null && (
        <div className="text-[11px] text-muted-foreground tabular-nums">
          + {PAID_LABEL} {paid.toLocaleString('ru-RU')}
        </div>
      )}
    </div>
  );
}
